/**
 * "Save my name": sign in with an emailed code so your name follows you to any device (accounts, Phase 2).
 *
 * Lives under the name box on the home screen. Three states:
 * - a guest: one line offering to save the name;
 * - signing in: email, then the 6-digit code from the email;
 * - signed in: "Signed in as Dave" with Sign out.
 */

import { type AccountInfo, type AuthErrorCode, LIMITS } from '@euchre/core';
import { h } from '../dom.js';
import { storeName } from '../net.js';
import { app, rerender, send, toast } from '../state.js';
import { storeDealerTalk } from './voice.js';
import { openProfile } from './profile.js';

type Step = 'closed' | 'email' | 'code' | 'birth';

/** Signed in and 18 or older (After Dark, Everyone profiles). */
export const isAdult = () => auth.account?.ageBand === '18+';

export const auth = {
  account: null as AccountInfo | null,
  step: 'closed' as Step,
  email: '',
  busy: false,
  error: '',
  /** The name the server last confirmed, to fall back on when a new name is refused. */
  serverName: '',
};

let box: HTMLElement | null = null;
let shownKey = '';

// ---------------------------------------------------------------------------
// Messages from the server
// ---------------------------------------------------------------------------

export function accountFromWelcome(account: AccountInfo | null | undefined, name: string): void {
  auth.serverName = name;
  if (account === undefined) return; // an older server
  auth.account = account;
  // After Dark is for signed-in adults only (Oct 22, 2026): anyone else goes back to Vegas.
  if (app.ui.dealerTalk === 'afterDark' && !isAdult()) {
    app.ui.dealerTalk = 'vegas';
    storeDealerTalk('vegas');
  }
  if (account) {
    // The account's name wins on every device.
    app.name = name;
    storeName(name);
    auth.step = 'closed';
  }
}

/** The server refused a new name (taken, or not allowed): put the confirmed one back. */
export function nameRefused(): void {
  if (!auth.serverName || auth.serverName === 'Player') return;
  app.name = auth.serverName;
  storeName(auth.serverName);
}

export function authCodeSent(email: string): void {
  auth.email = email;
  auth.step = 'code';
  auth.busy = false;
  auth.error = '';
}

export function authOk(account: AccountInfo, created: boolean): void {
  auth.account = account;
  auth.step = 'closed';
  auth.busy = false;
  auth.error = '';
  toast(created ? `Name saved! You're signed in as ${account.displayName}.` : `Welcome back, ${account.displayName}!`);
}

/** The code was right and this is a new account: ask the birth year. */
export function authNeedBirthYear(): void {
  auth.step = 'birth';
  auth.busy = false;
  auth.error = '';
}

export function authFailed(message: string, code: AuthErrorCode): void {
  auth.busy = false;
  auth.error = message;
  if (code === 'too_young') {
    auth.step = 'closed';
    toast(message, 9000);
  }
  // An expired or used-up code: go back to the email step so they can ask for a new one.
  if (code === 'expired' || code === 'too_many_tries') auth.step = 'email';
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

export function accountPanel(): HTMLElement {
  box ??= h('div', { class: 'account', attrs: { 'aria-live': 'polite' } });
  const key = JSON.stringify([auth.account, auth.step, auth.email, auth.busy, auth.error, app.conn]);
  if (key !== shownKey) {
    shownKey = key;
    box.replaceChildren(...render());
  }
  return box;
}

function birthYearForm(intro: string): Node[] {
  const online = app.conn === 'open';
  const year = h('input', {
    id: 'account-birth-year',
    class: 'field code-field',
    maxLength: 4,
    placeholder: '1985',
    autocomplete: 'bday-year',
    attrs: { inputmode: 'numeric', pattern: '[0-9]*' },
    oninput: () => {
      year.value = year.value.replace(/\D/g, '').slice(0, 4);
    },
  });
  focusSoon('#account-birth-year');
  return [
    h(
      'form',
      {
        class: 'account-form',
        onsubmit: (ev) => {
          ev.preventDefault();
          if (year.value.length !== 4 || auth.busy) return year.focus();
          auth.busy = true;
          auth.error = '';
          send({ type: 'auth_birth_year', year: Number(year.value) });
          rerender();
        },
      },
      h('label', { htmlFor: 'account-birth-year', text: 'Year you were born' }),
      h('p', { class: 'account-help', text: intro }),
      h('div', { class: 'row' }, year, h('button', { class: 'btn', type: 'submit', text: auth.busy ? 'Saving…' : 'Continue', disabled: auth.busy || !online })),
      auth.error ? h('p', { class: 'account-error', attrs: { role: 'alert' }, text: auth.error }) : null,
      auth.account ? null : h('p', { class: 'account-actions' }, h('button', { class: 'link-btn', type: 'button', text: 'Cancel', onclick: close })),
    ),
  ];
}

function render(): Node[] {
  const online = app.conn === 'open';
  if (auth.account && auth.account.ageBand === null) {
    return birthYearForm('One quick question for your saved name: we keep only whether you’re 13–17 or 18+, never the year.');
  }
  if (auth.step === 'birth' && !auth.account) {
    return birthYearForm('Saved names are for players 13 and older. We keep only whether you’re 13–17 or 18+, never the year.');
  }
  if (auth.account) {
    return [
      h(
        'p',
        { class: 'account-line' },
        'Signed in as ',
        h('strong', { text: auth.account.displayName }),
        h('span', { class: 'account-email', text: ` (${auth.account.email})` }),
        ' · ',
        h('button', { class: 'link-btn', type: 'button', text: 'Sign out', disabled: !online, onclick: signOut }),
        ' · ',
        h('button', { class: 'link-btn', type: 'button', text: 'My stats', disabled: !online, onclick: () => openProfile() }),
      ),
    ];
  }
  if (auth.step === 'closed') {
    return [
      h(
        'p',
        { class: 'account-line' },
        h('button', {
          class: 'link-btn',
          type: 'button',
          text: 'Save my name',
          disabled: !online,
          onclick: () => {
            auth.step = 'email';
            auth.error = '';
            rerender();
            focusSoon('#account-email');
          },
        }),
        ' so it’s yours on any phone or computer. Optional. · ',
        h('button', { class: 'link-btn', type: 'button', text: 'My stats', disabled: !online, onclick: () => openProfile() }),
      ),
    ];
  }
  const error = auth.error ? h('p', { class: 'account-error', attrs: { role: 'alert' }, text: auth.error }) : null;
  const cancel = h('button', { class: 'link-btn', type: 'button', text: 'Cancel', onclick: close });

  if (auth.step === 'email') {
    const email = h('input', {
      id: 'account-email',
      class: 'field',
      type: 'email',
      value: auth.email,
      maxLength: LIMITS.emailMax,
      placeholder: 'you@example.com',
      autocomplete: 'email',
    });
    return [
      h(
        'form',
        {
          class: 'account-form',
          onsubmit: (ev) => {
            ev.preventDefault();
            const value = email.value.trim();
            if (!value) return email.focus();
            // The account starts with the name in the box above, so make sure the server has it first.
            const nameBox = document.querySelector<HTMLInputElement>('#name');
            const name = nameBox?.value.trim() ?? app.name;
            if (!name) {
              toast('Type the name you want to keep first.');
              nameBox?.focus();
              return;
            }
            if (name !== app.name) {
              app.name = name;
              storeName(name);
              send({ type: 'set_name', name });
            }
            auth.email = value;
            auth.busy = true;
            auth.error = '';
            send({ type: 'auth_request_code', email: value });
            rerender();
          },
        },
        h('label', { htmlFor: 'account-email', text: 'Your email' }),
        h('p', { class: 'account-help', text: 'We’ll email you a 6-digit code. No password, no newsletters.' }),
        h(
          'div',
          { class: 'row' },
          email,
          h('button', { class: 'btn', type: 'submit', text: auth.busy ? 'Sending…' : 'Email me a code', disabled: auth.busy || !online }),
        ),
        error,
        h('p', { class: 'account-actions' }, cancel),
      ),
    ];
  }

  // step === 'code'
  const code = h('input', {
    id: 'account-code',
    class: 'field code-field',
    maxLength: LIMITS.codeLength,
    placeholder: '123456',
    autocomplete: 'one-time-code',
    attrs: { inputmode: 'numeric', pattern: '[0-9]*' },
    oninput: () => {
      code.value = code.value.replace(/\D/g, '').slice(0, LIMITS.codeLength);
      if (code.value.length === LIMITS.codeLength) submitCode(code.value);
    },
  });
  focusSoon('#account-code');
  return [
    h(
      'form',
      {
        class: 'account-form',
        onsubmit: (ev) => {
          ev.preventDefault();
          submitCode(code.value);
        },
      },
      h('label', { htmlFor: 'account-code', text: 'Code from the email' }),
      h('p', { class: 'account-help' }, 'Sent to ', h('strong', { text: auth.email }), '. It can take a minute; check spam too.'),
      h(
        'div',
        { class: 'row' },
        code,
        h('button', { class: 'btn', type: 'submit', text: auth.busy ? 'Checking…' : 'Sign in', disabled: auth.busy || !online }),
      ),
      error,
      h(
        'p',
        { class: 'account-actions' },
        h('button', {
          class: 'link-btn',
          type: 'button',
          text: 'Use a different email',
          onclick: () => {
            auth.step = 'email';
            auth.error = '';
            rerender();
          },
        }),
        ' · ',
        cancel,
      ),
    ),
  ];
}

function submitCode(value: string): void {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== LIMITS.codeLength || auth.busy) return;
  auth.busy = true;
  auth.error = '';
  send({ type: 'auth_verify_code', email: auth.email, code: digits });
  rerender();
}

function signOut(): void {
  send({ type: 'auth_sign_out' });
  toast('Signed out. You can keep playing as a guest.');
}

function close(): void {
  auth.step = 'closed';
  auth.error = '';
  auth.busy = false;
  rerender();
}

function focusSoon(selector: string): void {
  requestAnimationFrame(() => document.querySelector<HTMLInputElement>(selector)?.focus());
}
